import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import {
    IChecklist2ReportAnswer,
    IChecklist2ReportAnswerDetailsCheckpointGroup,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from '@fi-shared/types';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { groupBy } from '@fi-shared/utils/group-by';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';

@Component({
    selector: 'fi-checklist-detailed-answers-table',
    templateUrl: './detailed-answers-table.component.html',
    styleUrls: ['./detailed-answers-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist-detailed-answers-table' },
    standalone: false,
})
export class FiChecklistDetailedAnswersTableComponent
    extends FiInfiniteScrollDataSource<IChecklist2ReportAnswer>
    implements OnChanges
{
    @Input({ required: true }) answerDetails: IChecklist2ReportAnswer[] = [];
    @Input() isSummaryRowIncluded: boolean = true;
    @Input() totalComplianceResult: number;
    @Input() checkpointGroups: IChecklist2ReportAnswerDetailsCheckpointGroup[];
    @Input() isNotAnsweredLabelShown: boolean = true;
    @Input() isSectionSummaryVisible: boolean = true;
    @Input() isTotalSummaryVisible: boolean = true;

    protected skipInitSelectedSort = true;
    public readonly itemsListType: FiItemsListType = FiItemsListType.ChecklistReportOpenChecklistsDeviationsTable;
    public readonly objectType: ObjectTypes = ObjectTypes.Checklist2;
    //isGroupSorting: boolean = true;
    protected readonly groupIdProp: string = 'CheckpointGroupId';
    protected readonly groupSortColumnName: string = 'CheckpointGroupName';
    protected readonly numberOfRecordsToTake = Number.MAX_SAFE_INTEGER;

    private _checkpointGroups: { [key: string]: IChecklist2ReportAnswer }[];

    private fiFormatDate = inject(FiFormatDateService);

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        let change: SimpleChange = changes.answerDetails;
        if (change) {
            this.reloadData();
        }
    }

    public getListData(): Promise<IChecklist2ReportAnswer[]> {
        return Promise.resolve(this.answerDetails);
    }

    protected reloadData(loadArgs?: ILoadDataArgs): Promise<boolean> {
        const promise = super.reloadData(loadArgs);
        promise.then(() => {
            // init _checkpointGroups only on start or sorting change
            this._checkpointGroups = groupBy(this.answerDetails, this.groupIdProp);
        });
        return promise;
    }

    private isSortingBySection(): boolean {
        return this.selectedSort.active === this.groupSortColumnName || !this.selectedSort.active; // if 'active' is undefined - we use default sort from backend (by sections)
    }

    protected getSectionName(answer: IChecklist2ReportAnswer): string {
        if (!this.isSortingBySection()) {
            return answer.CheckpointGroupName;
        }

        const checkpointGroups = this._checkpointGroups;

        const sectionId: string = Object.keys(checkpointGroups).find((groupId: string) => {
            return checkpointGroups[groupId][0].CheckpointId === answer.CheckpointId;
        });

        return !!sectionId ? checkpointGroups[sectionId][0]?.CheckpointGroupName : '';
    }

    protected isScoringVisible(): boolean {
        return this.items().some((item: IChecklist2ReportAnswer) => item.IsComplianceThresholdEnabled && item.Scoring);
    }

    protected isSummaryRowHidden(answer: IChecklist2ReportAnswer): boolean {
        return (
            !this.isScoringVisible() ||
            !this.isComplianceDataAvailable() ||
            !(this.isSortingBySection() && this.isLastCheckpointInSection(answer)) ||
            !this.isSectionSummaryVisible
        );
    }

    private isComplianceDataAvailable(): boolean {
        return this.isSummaryRowIncluded && this.isComplianceThresholdEnabled();
    }

    protected isTotalSummaryDisplayed(): boolean {
        return this.isComplianceDataAvailable() && this.isTotalSummaryVisible && this.isScoringVisible();
    }

    private isComplianceThresholdEnabled(): boolean {
        return this.items().some((item: IChecklist2ReportAnswer) => item.IsComplianceThresholdEnabled);
    }

    private isLastCheckpointInSection(answer: IChecklist2ReportAnswer): boolean {
        const checkpointGroups = this._checkpointGroups;

        const result = isDefined(
            Object.keys(checkpointGroups).find((groupId: string) => {
                return (
                    checkpointGroups[groupId][checkpointGroups[groupId].length - 1].CheckpointId === answer.CheckpointId
                );
            }),
        );
        return result;
    }

    protected getCurrentPeriodInfo(answer: IChecklist2ReportAnswer): string {
        if (!answer.PeriodStartDateTime || !answer.PeriodDueDateTime || this.items().indexOf(answer) !== 0) {
            return '';
        }
        return (
            this.fiFormatDate.formatDate(answer.PeriodStartDateTime, FiDateTimeFormatOptions, { showToday: false }) +
            ' - ' +
            this.fiFormatDate.formatDate(answer.PeriodDueDateTime, FiDateTimeFormatOptions, { showToday: false })
        );
    }

    private getNonComplianceCount(isTotal: boolean, answer: IChecklist2ReportAnswer): number {
        if (!isTotal) {
            const currentCheckpointGroup = this.checkpointGroups?.find(
                (checkpointGroup) => checkpointGroup.CheckpointGroupId == answer.CheckpointGroupId,
            );
            if (currentCheckpointGroup && !isNullOrUndefined(currentCheckpointGroup.ComplianceResult)) {
                return Math.round(currentCheckpointGroup.ComplianceResult * 100);
            }
        } else {
            if (!isNullOrUndefined(this.totalComplianceResult)) {
                return Math.round(this.totalComplianceResult * 100);
            }
        }
        return undefined;
    }

    protected getNonCompliancePercentage(isTotal: boolean, answer: IChecklist2ReportAnswer): string {
        const nonComplianceCount: number = this.getNonComplianceCount(isTotal, answer);
        return isDefined(nonComplianceCount) ? `${nonComplianceCount}%` : '-';
    }

    protected getComplianceThresholdPercent(): string {
        const items = this.items();
        return items[0]?.ComplianceThreshold + '%';
    }

    protected getNonComplianceCountBadgeClass(isTotal: boolean, answer: IChecklist2ReportAnswer): { [key: string]: boolean } {
        const items = this.items();
        return {
            'non-compliance-border-gradient':
                this.getNonComplianceCount(isTotal, answer) < items[0]?.ComplianceThreshold,
        };
    }

    protected readonly FiDateTimeFormatOptions = FiDateTimeFormatOptions;
}
